export interface ArchiveCase {
  slug: string;
  category: string;
  title: string;
  kicker: string;
  excerpt: string;
  published: string;
  read: string;
  heat: number; // 1-5
  tags: string[];
  body: { h: string; p: string[] }[];
}

export interface ArchiveCategory {
  slug: string;
  name: string;
  en: string;
  tagline: string;
  desc: string;
  icon: string;
  accent: string;
  sample: string[];
}

export const CATEGORIES: ArchiveCategory[] = [
  {
    slug: "renpin",
    name: "人品争议",
    en: "Character",
    tagline: "表面功夫与真实底色之间，最容易翻车",
    desc: "从消费情怀、人设包装到私下言行，人品争议往往不是单一事件，而是长期积累的观感崩塌。我们把这类争议拆成可检索的小标签，帮你快速对应到具体表现。",
    icon: "ri-user-unfollow-line",
    accent: "primary",
    sample: ["伪善人设", "学历造假", "耍心机", "两面派", "恩将仇报"],
  },
  {
    slug: "ganqing",
    name: "感情纠纷",
    en: "Romance",
    tagline: "私人感情一旦摆上台面，就是一场公开的拉锯战",
    desc: "恋爱、婚姻、分手后的对线，向来是吃瓜热度最高的板块。梳理感情纠纷的起因、证据链与舆论反转，能看清风波是如何一步步被放大的。",
    icon: "ri-heart-3-line",
    accent: "accent",
    sample: ["出轨劈腿", "撕前任", "婚内纠纷", "分手互爆", "抚养权"],
  },
  {
    slug: "shuadapai",
    name: "耍大牌",
    en: "Diva",
    tagline: "被镜头拍到的「巨星脾气」，最能点燃大众怒火",
    desc: "迟到、罢演、苛刻排场、区别对待……耍大牌之所以让人反感，是因为它同时触动了职业素养与平等心态两条线。这里整理高发场景与处理争议的典型路径。",
    icon: "ri-temp-hot-line",
    accent: "accent",
    sample: ["现场耍横", "行程迟到", "压价坐地起价", "区别对待粉丝", "怼记者"],
  },
  {
    slug: "weiyue",
    name: "违约失信",
    en: "Breach",
    tagline: "合同与承诺的裂缝，往往直接砸向商业信誉",
    desc: "代言反水、擅自跑单、拖欠片酬、隐瞒翻车解除合作，违约失信既涉及契约精神，也牵涉商业利益与法律追责，是风险最实在的一类。",
    icon: "ri-file-shield-2-line",
    accent: "primary",
    sample: ["代言反水", "解约纠纷", "拖欠工资", "合同造假", "跑路失联"],
  },
  {
    slug: "yanlun",
    name: "言论翻车",
    en: "Foot-in-mouth",
    tagline: "一句话的杀伤力，可能大于十年作品积累",
    desc: "直播口误、采访失言、旧闻考古、小号被扒，言论翻车最考验公关速度。我们按「翻车前因—发酵路径—下场处理」还原整套舆情链路。",
    icon: "ri-question-answer-line",
    accent: "primary",
    sample: ["失言翻车", "旧照考古", "直播口误", "阴阳怪气", "立场争议"],
  },
  {
    slug: "xingwei",
    name: "行为失当",
    en: "Conduct",
    tagline: "镜头之外的行为，最终都会被镜头追责",
    desc: "酒驾、打架、违规营业、公德失范等行为失当，通常是实锤最硬、下沉最快的一类。它往往伴随公共安全与法律红线，处置结果也最具示范性。",
    icon: "ri-shield-user-line",
    accent: "primary",
    sample: ["酒驾斗殴", "公共场合失态", "占用公共资源", "违规营业", "虐待动物"],
  },
];

export const CASES: ArchiveCase[] = [
  {
    slug: "renpin-faces-archive",
    category: "renpin",
    title: "「两面派」档案：镜头外的反差究竟怎么被扒出来的",
    kicker: "人品争议 · 观感崩塌路径",
    excerpt: "从考古旧帖到工作人员爆料，人设反差的证据链往往环环相扣。还原大众对「表里不一」最反感的几个瞬间，是理解人品争议的起点。",
    published: "2026-09-05",
    read: "8 分钟",
    heat: 5,
    tags: ["两面派", "伪善人设", "工作人员爆料", "考古旧料"],
    body: [
      { h: "反差感从何而来", p: ["人品争议最常见的触发点，是公众眼中的人设与私下可被验证的行为之间存在巨大落差。那些常年被镜头包装成「暖男」「老实人」「宠粉」的形象，一旦出现一段私下录音、一张聊天截图或一位工作人员的实名证词，落差便会被瞬间点燃。", "围观群众真正愤怒的往往不是行为本身，而是「被欺骗」的感受——过去所有正面的公关投入，此刻都变成了反噬的燃料。"] },
      { h: "证据是如何拼凑起来的", p: ["这类风波通常不会靠单一实锤定论，而是多线证据互相印证：旧帖考古、同框分析、前同事的只言片语、合同附件与行程记录，甚至多年前的公开采访，都可能被重新拉出来对照。", "当数条独立证据指向同一结论时，大众的观感就会从「将信将疑」滑向「实锤」。这也是为什么人品争议往往最难通过发声明扭转——它挑战的是信任，而信任一旦破裂就难以重建。"] },
      { h: "信任重建为何如此困难", p: ["应对人品争议，声明只能算及格线。真正决定风评走向的，是后续长期可被观察的行为是否兑现。消失一段时间、专注作品、少立人设，反而比高调喊话更容易被时间稀释。", "对艺人而言，最好的「公关」其实是作品与日常行为的长期一致性；对人品争议的观察者而言，等待时间给出的答案，往往比围观情绪更接近真相。"] },
    ],
  },
  {
    slug: "renpin-edge-labor",
    category: "renpin",
    title: "边界试探：把心机写进剧本，还是写进生活？",
    kicker: "人品争议 · 博弈与翻脸",
    excerpt: "圈内竞争与合作中的小动作一旦被曝光，很难用一句「误会」带过。梳理常见的心机话术与对线剧本，看清谁在借势，谁在挖坑。",
    published: "2026-08-28",
    read: "6 分钟",
    heat: 4,
    tags: ["耍心机", "抢资源", "踩高捧低", "塑料情"],
    body: [
      { h: "小动作为什么藏不住", p: ["在高度公开的行业里，几乎不存在真正的「私下」。同框时的微表情、采访中的阴阳话术、团队放风的匿名稿，都可能成为后续对线的伏笔。", "当两位当事人公开博弈时，围观者会逐帧比对谁先翻脸、谁在带节奏、谁拿出的证据更有分量——这也让每一次「暗战」都变成一次全民参与的证据战。"] },
      { h: "舆论站的三种结局", p: ["多数博弈会在经纪人协商后低调收场；部分会演变成持续数周的隔空喊话；只有极少数会走向法律程序。结局往往取决于手握实锤的一方是否愿意「掀桌」。", "对普通观众而言，与其站队，不如先看清双方各自的利益诉求——很多看似激烈的对峙，最终不过是一场互相妥协的生意。"] },
    ],
  },
  {
    slug: "ganqing-recording",
    category: "ganqing",
    title: "分手对线全纪录：公开恋情之后的舆论战场",
    kicker: "感情纠纷 · 官宣与反噬",
    excerpt: "当私密的感情被摆上公共舆论场，甜蜜滤镜与翻脸速度往往同样惊人。从官宣时间线到证据互曝，拆解一段高调恋情的完整塌房链路。",
    published: "2026-09-02",
    read: "9 分钟",
    heat: 5,
    tags: ["撕前任", "官宣时间线", "分手互爆", "聊天截图"],
    body: [
      { h: "高调官宣埋下的伏笔", p: ["越是把恋情当作流量资产的公开情侣，分手时的撕裂就越彻底。官宣时排场越大、人设绑定越深，散场时可供反噬的素材就越多——每一张同框、每一条情话，都会在分手后变成刀。", "感情纠纷的导火索往往是双方对「分手剧本」的预期不一致：一方想体面退场，另一方想争夺舆论高地，于是先发制人成了常见策略。"] },
      { h: "证据的攻防逻辑", p: ["聊天记录的时间、说话的语气、转账的金额，都会被逐条解读。先曝料的一方常把对方塑造成过错方，而后发的一方则用「完整性截图」与律师函试图扳回。", "真正让舆论天平倾斜的，是证据能否自洽、时间线能否闭合。孤证与剪辑过的片段，往往反而会激起大众的逆反心理。"] },
      { h: "吃瓜之外的冷思考", p: ["感情纠纷里没有绝对赢家，围观的高潮过去后，留下的常常是两败俱伤的名誉与一场漫长诉讼。对当事人而言，及时止损、交由法律而非舆论裁决，才是更体面的离场方式。"] },
    ],
  },
  {
    slug: "ganqing-privacy",
    category: "ganqing",
    title: "当私生活被搬到台前：隐私边界与公众窥私的拉扯",
    kicker: "感情纠纷 · 隐私与曝光",
    excerpt: "并非每一段感情都愿意被围观，但一旦进入公众视野，隐私就成了稀缺品。讨论「自愿曝光」与「被动卷入」之间的灰色地带。",
    published: "2026-08-19",
    read: "5 分钟",
    heat: 3,
    tags: ["隐私曝光", "素人卷入", "家人牵扯", "代拍跟拍"],
    body: [
      { h: "被动卷入的素人困境", p: ["最让人揪心的感情纠纷，往往牵连到毫无公关能力的素人——恋人的前任、家庭、好友被卷入舆论漩涡，却没有任何团队替他们发声。", "当私人照片与家庭信息被无差别搬运，伤害的已不只是当事人，而是整个家庭的基本生活秩序。"] },
      { h: "围观与窥私的边界", p: ["吃瓜本身是娱乐，但当信息涉及未成年人、家庭成员或未经证实的私密细节时，传播就跨过了红线。理性吃瓜的第一步，是把「该不该传播」放在「想不想知道」前面。", "对平台与自媒体而言，真正的专业度不在蹭热点的速度，而在面对隐私素材时愿意按下不发的克制。"] },
    ],
  },
  {
    slug: "shuadapai-location",
    category: "shuadapai",
    title: "片场「大牌」实录：从行程迟到到现场甩脸",
    kicker: "耍大牌 · 现场失控",
    excerpt: "迟到让全组干等、对工作人员颐指气使、因临时加价罢演——这些片场细节被曝光后，职业素养议题总是迅速冲上热搜。",
    published: "2026-08-30",
    read: "7 分钟",
    heat: 4,
    tags: ["现场耍横", "行程迟到", "区别对待", "罢演"],
    body: [
      { h: "被放大的不是脾气，是态度", p: ["耍大牌之所以激起公愤，核心不在艺人「有没有权利提出要求」，而在ta对普通工作人员的态度。对导演客气、对场务吼叫的双重标准，最能击穿大众的同理心。", "片场是协作现场，任何一个人的拖延都在损耗全组的精力。一次迟到背后，是数十上百位幕后人员被拉长的工时。"] },
      { h: "爆料的可信度如何判断", p: ["现场爆料常以工作人员的匿名口述、工作群截图或场务证词形式出现，可信度参差不齐。判断的关键在于：描述是否具体、是否与其他场次目击交叉印证、是否有可核查的时间与地点。", "当多个剧组、多批工作人员给出相似描述时，就不能再简单用「竞争对手抹黑」来解释。"] },
    ],
  },
  {
    slug: "shuadapai-red-carpet",
    category: "shuadapai",
    title: "红毯与采访台：镜头前那一秒的傲慢如何破功",
    kicker: "耍大牌 · 镜头放大",
    excerpt: "直播镜头最诚实——采访黑脸、无视提问、抢主持人话，这些不经意的傲慢在无剪辑直播里无所遁形，并迅速被做成一屏屏表情包。",
    published: "2026-08-12",
    read: "5 分钟",
    heat: 3,
    tags: ["怼记者", "采访黑脸", "直播翻车", "表情包出圈"],
    body: [
      { h: "直播为何成为照妖镜", p: ["录播可以剪辑，直播却不能。一场大型直播活动里，主持人的临场救场与艺人的不耐烦同框出现，反差的传播力远超任何通稿。", "加上弹幕与实时评论的加成，任何微表情都可能被切片、放大、二创，最终定型为一个挥之不去的网络标签。"] },
      { h: "公关与本人的角力", p: ["事后团队常以「身体不适」「行程疲惫」「剪辑断章取义」解释，但直播原片摆在那里，公关空间极为有限。", "真正能挽回的，只有艺人本人带着真诚的公开回应，以及后续被反复验证的言行改善。傲慢的镜头瞬间，往往需要数年的谦逊才能赎回。"] },
    ],
  },
  {
    slug: "weiyue-endorsement",
    category: "weiyue",
    title: "代言风暴：品牌连夜解约背后的商业逻辑",
    kicker: "违约失信 · 商业反噬",
    excerpt: "从代言人翻车到品牌方极速切割，短短数小时完成的解约流程，背后是合同中的「道德条款」与商业风险的精密权衡。",
    published: "2026-09-01",
    read: "8 分钟",
    heat: 5,
    tags: ["代言反水", "品牌解约", "道德条款", "赔偿金"],
    body: [
      { h: "为什么品牌总在第一时间解约", p: ["当代言人陷入重大争议，品牌方往往在声明中强调「已第一时间终止合作」。这既是对公众情绪的表态，更是合同里道德条款被触发的常规操作。", "品牌与明星本质是相互背书的关系，一旦后者信誉受损，继续合作带来的负面联想，会远超已支付的代言费——及时止损是理性的商业决策。"] },
      { h: "解约的连锁反应", p: ["一位顶流代言人的翻车，常牵动数十个品牌的集体切割，进而波及待播作品、综艺排期与已签约项目。商业链条上的每个人都必须重新评估风险敞口。", "这也反过来抬高了后续合作的审核门槛：代言、选角开始更看重艺人的长期稳定度，而非短期的热度与流量。"] },
    ],
  },
  {
    slug: "weiyue-schedule",
    category: "weiyue",
    title: "跑单与拖款：合作信用崩塌的典型样本",
    kicker: "违约失信 · 契约失守",
    excerpt: "临开拍前反悔、拖欠片酬尾款、宣传期擅自缺席……契约失信虽不如八卦劲爆，却直接动摇了行业最基本的信任基础。",
    published: "2026-08-15",
    read: "6 分钟",
    heat: 3,
    tags: ["临期毁约", "拖欠片酬", "跑路失联", "行业黑名单"],
    body: [
      { h: "比八卦更实在的风险", p: ["与情绪化的争议不同，违约失信是可以用合同、流水与诉讼记录验证的硬伤。一次跑单，可能让一个中小制作团队直接陷入资金断裂的困境。", "这类风波往往不靠热搜传播，而靠从业者之间的口口相传，最终沉淀为行业内部的合作黑名单。"] },
      { h: "如何识别契约风险", p: ["对行业外的观众而言，能从公开渠道看到的只是冰山一角：官司立案公告、被执行信息、合作方公开催讨。对从业者而言，尽调与合同审慎才是真正的防线。", "契约精神的缺失很难用公关弥补，因为信任一旦在商业层面受损，几乎不存在「翻篇」的空间。"] },
    ],
  },
  {
    slug: "yanlun-live-misstep",
    category: "yanlun",
    title: "直播口误与敏感词：一句话引爆的全链路复盘",
    kicker: "言论翻车 · 发酵路径",
    excerpt: "从直播间的一句口误到全网二创、再到团队删评，言论翻车往往在几小时内走完「萌芽—扩散—失控」全过程，考验的是团队的实时反应力。",
    published: "2026-09-06",
    read: "7 分钟",
    heat: 4,
    tags: ["直播口误", "失言翻车", "二创出圈", "删评控评"],
    body: [
      { h: "发酵速度为何这么快", p: ["直播与短视频天然具备高传播性，一句有歧义的话会在极短时间内被切片、加字幕、配上 BGM 二次传播，语境被彻底抽离，只剩最具冲击力的几个字。", "加上评论区与热搜的推波助澜，一次口误的传播半径，常远超团队原本设想的「小范围失误」。"] },
      { h: "回应时机决定成败", p: ["黄金回应窗口通常只有数小时。反应过慢会被解读为「默认」，反应过快又可能因为语境不清而二次翻车。好的回应要先还原完整语境，再明确立场，而非空洞道歉。", "真正拉长时间来看，一次口误是否致命，取决于艺人过往言行的整体信誉。偶发失误加上诚恳态度，往往能被时间稀释；若历史黑料累累，则任何解释都会显得苍白。"] },
    ],
  },
  {
    slug: "yanlun-archeology",
    category: "yanlun",
    title: "旧帖考古：多年前的发言为何在今天被翻出",
    kicker: "言论翻车 · 时间线回溯",
    excerpt: "社交媒体最大的风险是「永不过期」。多年前的偏激发言、青涩价值观或站队记录，会在某个人设高光时刻被集中翻出，制造巨大的割裂感。",
    published: "2026-08-22",
    read: "6 分钟",
    heat: 4,
    tags: ["旧帖考古", "站队记录", "黑历史", "人设割裂"],
    body: [
      { h: "谁在推动考古", p: ["旧帖被翻出，有时是看不惯的网友自发行为，有时则是竞争对手的有组织操作。无论来源如何，一旦时间线与当下言论形成鲜明对照，传播就停不下来。", "考古素材的杀伤力在于「真实」——那是当事人自己写下的话，无法用「被人断章取义」来推脱。"] },
      { h: "成长与甩锅的边界", p: ["当事人常用「那时候还年轻」「当时不懂事」来应对考古。公众是否买账，取决于翻出内容的恶劣程度与时间跨度——十年前的玩笑或许可以被理解，涉及原则立场的内容则很难用一句「成长」带过。", "这也给所有公众人物上了一课：网络没有真正意义的「前科清零」，你写下的每一个字，都在为未来的人设做背书。"] },
    ],
  },
  {
    slug: "xingwei-drunk-driving",
    category: "xingwei",
    title: "酒驾与打架：把公共安全风险写进履历的代价",
    kicker: "行为失当 · 法律红线",
    excerpt: "酒驾、斗殴、危险驾驶等行为直接触碰法律红线，一旦实锤几乎不存在公关空间，事业通常以断崖式坠落收场。",
    published: "2026-09-08",
    read: "7 分钟",
    heat: 5,
    tags: ["酒驾", "斗殴", "危险驾驶", "行政处罚"],
    body: [
      { h: "法律红线面前没有滤镜", p: ["行为失当里最「实」的一类，莫过于酒驾、斗殴这类已被执法记录、有明确处罚结果的事件。它们不以舆论意志为转移，公共安全与法律底线决定了处理结果的严肃性。", "一旦进入执法程序，明星身份反而成为放大镜——公众会格外关注处置是否「一视同仁」，任何网开一面的迹象都会引发次生舆情。"] },
      { h: "坠落之后的复出难题", p: ["触碰法律红线的艺人，复出之路远比一般翻车漫长。作品下架、代言清零、综艺除名是最直接的惩罚，而更深的代价是社会信任的长期缺失。", "对行业而言，这类事件最大的意义在于示范作用——它清楚地向从业者传递了一个信号：镜头前的光环，永远不能凌驾于法律与公德之上。"] },
    ],
  },
  {
    slug: "xingwei-public-conduct",
    category: "xingwei",
    title: "公德失范：占用公共资源与日常失态合集",
    kicker: "行为失当 · 公德审视",
    excerpt: "机场拥堵、公共场合争执、粉丝应援失控等看似「小事」的行为，一旦被贴上占用公共资源的标签，同样会演变为高热度争议。",
    published: "2026-08-06",
    read: "5 分钟",
    heat: 3,
    tags: ["占用公共资源", "机场拥堵", "应援失控", "公共场合失态"],
    body: [
      { h: "小事为何会被放大", p: ["明星的一举一动都自带公共属性。当个人行程造成机场拥堵、粉丝应援扰民，或艺人在公共场合失态，这些「个人行为」便因占用公共资源而变成了公共议题。", "大众的反感不针对享受人气本身，而针对「特权感」——那种把公共秩序让位于个人排场的理所当然。"] },
      { h: "代拍与饭圈的灰色地带", p: ["很多时候，现场拥堵的始作俑者是代拍与疯狂粉丝，艺人反而处于被动。但艺人团队对粉丝行为的默许或引导，也常被拿来追问责任。", "真正专业的团队会主动引导秩序、约束代拍、在公开场合保持克制——把「不给他人添麻烦」当作最基本的职业操守。"] },
    ],
  },
];

export const HOT_ARCHIVE: string[] = [
  "renpin-faces-archive",
  "xingwei-drunk-driving",
  "weiyue-endorsement",
  "ganqing-recording",
  "yanlun-live-misstep",
  "shuadapai-location",
];

export const STAGES = [
  { title: "萌芽：蛛丝马迹", icon: "ri-focus-3-line", text: "一切风波都始于不起眼的细节——一句奇怪的发言、一张同框图、一条匿名吐槽，大众的嗅觉最先捕捉到异样。" },
  { title: "发酵：实锤进场", icon: "ri-fire-line", text: "录音、截图、合同、执法记录等硬证据逐步公开，舆论从猜测转为站队，话题冲上热搜，商业与作品的连锁反应开始显现。" },
  { title: "失控：全面清算", icon: "ri-flashlight-line", text: "品牌解约、作品下架、综艺除名同步推进，过往言行被考古清算，公关口径失效，艺人进入「不能说话、说了也没人信」的阶段。" },
  { title: "沉淀：时间裁决", icon: "ri-timer-line", text: "热度退潮不等于翻篇，真正决定结局的是法律程序与社会共识的最终沉淀——有人被遗忘，有人被记住，有人付出代价。" },
];